import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Mic, X, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { cn } from "@/lib/utils";

interface SearchResult {
  id: string;
  title: string;
  type: "listing" | "article" | "video" | "trainer";
  slug?: string;
}

export const SearchBar = ({ className }: { className?: string }) => {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<SearchResult[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const navigate = useNavigate();
  const searchRef = useRef<HTMLDivElement>(null);
  const recognitionRef = useRef<any>(null);

  useEffect(() => {
    // Initialize Web Speech API
    if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
      const SpeechRecognition = (window as any).webkitSpeechRecognition || (window as any).SpeechRecognition;
      recognitionRef.current = new SpeechRecognition();
      recognitionRef.current.continuous = false;
      recognitionRef.current.interimResults = false;
      recognitionRef.current.lang = 'es-ES';

      recognitionRef.current.onresult = (event: any) => {
        const transcript = event.results[0][0].transcript;
        setQuery(transcript);
        setIsRecording(false);
      };

      recognitionRef.current.onerror = () => {
        setIsRecording(false);
      };

      recognitionRef.current.onend = () => {
        setIsRecording(false);
      };
    }
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setShowResults(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (query.length < 2) {
      setResults([]);
      return;
    }

    const searchTimeout = setTimeout(async () => {
      setIsLoading(true);
      try {
        const searchTerm = `%${query}%`;

        const [listingsRes, articlesRes, videosRes, trainersRes] = await Promise.all([
          supabase
            .from("listings")
            .select("id, name, slug")
            .eq("status", "active")
            .ilike("name", searchTerm)
            .limit(3),
          supabase
            .from("articles")
            .select("id, title, slug")
            .eq("status", "published")
            .ilike("title", searchTerm)
            .limit(3),
          supabase
            .from("videos")
            .select("id, titulo, slug")
            .eq("activo", true)
            .ilike("titulo", searchTerm)
            .limit(3),
          supabase
            .from("trainer_profiles_public")
            .select("id, full_name, slug")
            .ilike("full_name", searchTerm)
            .limit(3),
        ]);

        const allResults: SearchResult[] = [
          ...(listingsRes.data || []).map((l) => ({
            id: l.id,
            title: l.name,
            type: "listing" as const,
            slug: l.slug,
          })),
          ...(articlesRes.data || []).map((a) => ({
            id: a.id,
            title: a.title,
            type: "article" as const,
            slug: a.slug,
          })),
          ...(videosRes.data || []).map((v) => ({
            id: v.id,
            title: v.titulo,
            type: "video" as const,
            slug: v.slug,
          })),
          ...(trainersRes.data || []).map((t) => ({
            id: t.id,
            title: t.full_name,
            type: "trainer" as const,
            slug: t.slug,
          })),
        ];

        setResults(allResults);
        setShowResults(true);
      } catch (error) {
        console.error("Search error:", error);
      } finally {
        setIsLoading(false);
      }
    }, 300);

    return () => clearTimeout(searchTimeout);
  }, [query]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (query.trim()) {
      navigate(`/search?q=${encodeURIComponent(query.trim())}`);
      setShowResults(false);
      setQuery("");
    }
  };

  const handleSelectResult = (result: SearchResult) => {
    setShowResults(false);
    setQuery("");
    
    switch (result.type) {
      case "listing":
        navigate(`/listing/${result.slug || result.id}`);
        break;
      case "article":
        navigate(`/articulo/${result.slug}`);
        break;
      case "video":
        navigate(`/video/${result.slug}`);
        break;
      case "trainer":
        navigate(`/trainer/${result.slug || result.id}`);
        break;
    }
  };

  const startVoiceSearch = () => {
    if (recognitionRef.current && !isRecording) {
      setIsRecording(true);
      recognitionRef.current.start();
    }
  };

  const stopVoiceSearch = () => {
    if (recognitionRef.current && isRecording) {
      recognitionRef.current.stop();
      setIsRecording(false);
    }
  };

  const clearSearch = () => {
    setQuery("");
    setResults([]);
    setShowResults(false);
  };

  const getTypeLabel = (type: string) => {
    switch (type) {
      case "listing":
        return "Gimnasio";
      case "article":
        return "Artículo";
      case "video":
        return "Video";
      case "trainer":
        return "Entrenador";
      default:
        return "";
    }
  };

  return (
    <div ref={searchRef} className={cn("relative", className)}>
      <form onSubmit={handleSearch} className="relative">
        <div className="relative flex items-center">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            type="text"
            placeholder="Buscar gimnasios, entrenadores, artículos, videos..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onFocus={() => results.length > 0 && setShowResults(true)}
            className="pl-10 pr-20"
          />
          <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1">
            {isLoading && <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />}
            {query && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={clearSearch}
                className="h-7 w-7 p-0"
                aria-label="Limpiar búsqueda"
              >
                <X className="w-4 h-4" aria-hidden="true" />
              </Button>
            )}
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={isRecording ? stopVoiceSearch : startVoiceSearch}
              className={cn("h-7 w-7 p-0", isRecording && "text-destructive animate-pulse")}
              aria-label={isRecording ? "Detener búsqueda por voz" : "Buscar por voz"}
            >
              <Mic className="w-4 h-4" aria-hidden="true" />
            </Button>
          </div>
        </div>
      </form>

      {showResults && results.length > 0 && (
        <div className="absolute top-full mt-2 w-full bg-popover rounded-md border shadow-lg z-50 max-h-[400px] overflow-y-auto">
          <Command>
            <CommandList>
              <CommandEmpty>No se encontraron resultados</CommandEmpty>
              <CommandGroup>
                {results.map((result) => (
                  <CommandItem
                    key={`${result.type}-${result.id}`}
                    onSelect={() => handleSelectResult(result)}
                    className="cursor-pointer"
                  >
                    <div className="flex items-center justify-between w-full">
                      <span className="truncate">{result.title}</span>
                      <span className="text-xs text-muted-foreground ml-2 flex-shrink-0">
                        {getTypeLabel(result.type)}
                      </span>
                    </div>
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
          {results.length > 0 && (
            <div className="p-2 border-t">
              <Button
                variant="ghost"
                size="sm"
                onClick={handleSearch}
                className="w-full text-xs"
              >
                Ver todos los resultados para "{query}"
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
